<div
    ngModelGroup="app-service-delivery-point"
    id="serviceDeliveryPointForm"
    data-automation="serviceDeliveryPointForm"
    [formGroup]="serviceDeliveryPointForm"
>
    <div class="form-group columns-container">
        <!--- SITE --->
        <mat-form-field class="column" appearance="outline" floatLabel="always">
            <mat-label data-automation="siteLabel">{{ 'utility.account.site' | translate }}</mat-label>
            <mat-select
                formControlName="siteId"
                data-automation="site"
                [class.loading-field]="shouldShowLoader()"
                required
                data-url-persist
                id="site"
                placeholder="{{
                    (sites | async).length > 0
                        ? ('utility.account.create.site_selector' | translate)
                        : ('utility.account.create.no_sites' | translate)
                }}"
            >
                <ng-container *ngFor="let site of sites | async">
                    <mat-option [value]="site.id" id="{{ site.id }}">{{ site.displayLabel }} </mat-option>
                </ng-container>
            </mat-select>
            <mat-error *ngIf="serviceDeliveryPointForm.get('siteId').hasError('required')">
                <span data-automation="siteValidationMessage">{{
                    'utility.account.validation.required' | translate
                }}</span>
            </mat-error>
            <mat-hint *ngIf="shouldShowLoader()" align="end">{{ 'utility.account.loading' | translate }}</mat-hint>
            <mat-spinner class="select-spinner" *ngIf="shouldShowLoader()" matSuffix diameter="20"></mat-spinner>
        </mat-form-field>
        <!-- NAME -->
        <form
            #multiLocaleLabels="ngForm"
            class="mat-form-field-container form-entry column"
            data-automation="displayLabel"
        >
            <multi-locale-input
                name="displayLabels"
                data-automation="sdpName"
                [inputLabel]="nameLabel"
                [(ngModel)]="multidisplayLabels"
                [locales]="serviceDeliveryPointForm.get('supportedLocales').value"
                [displayLabelKey]="multiLocaleConfig.displayLabelKey"
                [localeKey]="multiLocaleConfig.localeKey"
                [placeholderText]="namePlaceholder"
                [defaultLocale]="multiLocaleConfig.defaultLocale"
                [required]="true"
                [id]="'sdp_display_labels'"
                #multiDisplaylabels
            >
            </multi-locale-input>
        </form>
        <!-- DEFAULT LOCALE -->
        <mat-form-field class="column" appearance="outline" floatLabel="always">
            <mat-label data-automation="localeLabel">{{ 'utility.account.default_locale' | translate }}</mat-label>
            <mat-select
                required
                formControlName="defaultLocale"
                data-automation="locale"
                placeholder="{{ 'utility.account.create.placeholder.locale_selector' | translate }}"
            >
                <mat-option
                    *ngFor="let supportedLocale of serviceDeliveryPointForm.get('supportedLocales').value"
                    [value]="supportedLocale.localeName"
                >
                    {{ supportedLocale.displayLabel }}
                </mat-option>
            </mat-select>
            <mat-error class="alert" *ngIf="serviceDeliveryPointForm.get('defaultLocale').hasError('required')">
                <span data-automation="localeValidationMessage">{{
                    'utility.account.validation.required' | translate
                }}</span>
            </mat-error>
        </mat-form-field>

        <!-- SUPPORTED LOCALES -->
        <mat-form-field class="column" appearance="outline" floatLabel="always" data-automation="supportedLocalesField">
            <mat-label data-automation="supportedLocalesLabel">{{
                'utility.account.supported_locales' | translate
            }}</mat-label>
            <mat-select
                formControlName="supportedLocales"
                data-automation="supportedLocales"
                (selectionChange)="handleSupportedLocalesSelection()"
                (openedChange)="supportedLocalesMenuToggled($event)"
                multiple
            >
                <mat-option
                    *ngFor="let locale of locales"
                    [value]="locale"
                    [disabled]="limitLocaleSelectList(5, locale) || onlyLocaleSelected(locale)"
                >
                    {{ locale.displayLabel }}
                </mat-option>
            </mat-select>
        </mat-form-field>

        <!-- COMMODITY -->
        <mat-form-field class="column" floatLabel="always" appearance="outline">
            <mat-label data-automation="commodityLabel">{{ 'utility.account.commodity' | translate }}</mat-label>
            <mat-select
                required
                formControlName="commodity"
                data-automation="commodity"
                id="commodity"
                placeholder="{{ 'utility.account.create.placeholder.commodity' | translate }}"
            >
                <mat-option [value]="null">{{ 'utility.account.create.placeholder.commodity' | translate }}</mat-option>
                <mat-option *ngFor="let commodity of commodities" [value]="commodity.id">
                    {{ commodity.displayLabel | translate }}
                </mat-option>
            </mat-select>
            <mat-error class="alert" *ngIf="serviceDeliveryPointForm.get('commodity').hasError('required')">
                <span data-automation="commodityValidationMessage">{{
                    'utility.account.validation.required' | translate
                }}</span>
            </mat-error>
        </mat-form-field>

        <!-- TYPE -->
        <mat-form-field class="column" floatLabel="always" appearance="outline">
            <mat-label data-automation="sdpTypeLabel">{{ 'utility.account.type' | translate }}</mat-label>
            <mat-select
                required
                formControlName="sdpType"
                data-automation="sdpType"
                placeholder="{{ 'utility.account.create.placeholder.type' | translate }}"
            >
                <mat-option *ngFor="let type of types; let i = index" [value]="type.name">
                    {{ type.displayLabel | translate }}
                </mat-option>
            </mat-select>
            <mat-error class="alert" *ngIf="serviceDeliveryPointForm.get('sdpType').hasError('required')">
                <span data-automation="typeValidationMessage">{{
                    'utility.account.validation.required' | translate
                }}</span>
            </mat-error>
        </mat-form-field>

        <!-- UTILITY METER NUMBER -->
        <mat-form-field class="column" appearance="outline" floatLabel="always">
            <mat-label data-automation="utilityMeterNumberLabel">{{
                'utility.account.utility_meter_number' | translate
            }}</mat-label>
            <input
                matInput
                id="utilityMeterNumber"
                type="text"
                name="utilityMeterNumber"
                placeholder="{{ 'utility.account.create.placeholder.utility_meter_number' | translate }}"
                formControlName="utilityMeterNumber"
                data-automation="utilityMeterNumber"
                data-url-persist
            />
            <mat-error *ngIf="serviceDeliveryPointForm.get('utilityMeterNumber').hasError('required')">
                <span data-automation="utilityMeterValidationMessage">{{
                    'utility.account.validation.required' | translate
                }}</span>
            </mat-error>
        </mat-form-field>

        <!-- START DATE -->
        <mat-form-field class="column" appearance="outline" floatLabel="always">
            <mat-label data-automation="startDttmLabel">{{ 'utility.account.start_dttm' | translate }}</mat-label>
            <input
                matInput
                required
                [matDatepicker]="startDttmDatepicker"
                placeholder="{{ 'utility.account.create.placeholder.start_dttm' | translate }}"
                formControlName="startDttm"
                data-automation="startDttm"
                (dateChange)="checkDates()"
            />
            <mat-datepicker-toggle matSuffix [for]="startDttmDatepicker">
                <mat-icon matDatepickerToggleIcon fontSet="material-icons-outlined">calendar_today</mat-icon>
            </mat-datepicker-toggle>
            <mat-datepicker #startDttmDatepicker></mat-datepicker>
            <mat-error class="alert" *ngIf="serviceDeliveryPointForm.get('startDttm').hasError('required')">
                <span data-automation="startDttmValidationMessage">{{
                    'utility.account.validation.required' | translate
                }}</span>
            </mat-error>
        </mat-form-field>

        <!-- END DATE -->
        <mat-form-field #endDate class="column" appearance="outline" floatLabel="always">
            <mat-label data-automation="endDttmLabel">{{ 'utility.account.end_dttm' | translate }}</mat-label>
            <input
                matInput
                [min]="getMinDate()"
                [matDatepicker]="endDttmDatepicker"
                placeholder="{{ 'utility.account.create.placeholder.end_dttm' | translate }}"
                formControlName="endDttm"
                data-automation="endDttm"
                (dateChange)="checkDates()"
            />
            <mat-datepicker-toggle matSuffix [for]="endDttmDatepicker">
                <mat-icon matDatepickerToggleIcon fontSet="material-icons-outlined">calendar_today</mat-icon>
            </mat-datepicker-toggle>
            <mat-datepicker #endDttmDatepicker></mat-datepicker>
            <mat-error class="alert" *ngIf="dateError">
                <span data-automation="endDttmValidationMessage">
                    {{ 'utility.account.validation.end_date' | translate }}
                </span>
            </mat-error>
        </mat-form-field>

        <!-- STATUS -->
        <mat-form-field class="column" appearance="outline">
            <mat-label data-automation="statusLabel">{{ 'utility.account.status' | translate }}</mat-label>
            <mat-select
                required
                formControlName="status"
                data-automation="status"
                placeholder="{{ 'utility.account.create.placeholder.status' | translate }}"
            >
                <mat-option *ngFor="let status of statuses" [value]="status.id">
                    {{ status.displayLabel | translate }}
                </mat-option>
            </mat-select>
            <mat-error *ngIf="serviceDeliveryPointForm.get('status').hasError('required')">
                <span data-automation="statusValidationMessage">{{
                    'utility.account.validation.required' | translate
                }}</span>
            </mat-error>
        </mat-form-field>

        <!-- DESCRIPTION -->
        <form
            #multiLocaleDescriptionLabels="ngForm"
            class="mat-form-field-container form-entry column"
            data-automation="descriptions"
        >
            <multi-locale-input
                name="descriptions"
                [inputLabel]="descriptionLabel"
                [(ngModel)]="multiDescriptionDisplayLabels"
                [locales]="serviceDeliveryPointForm.get('supportedLocales').value"
                [displayLabelKey]="multiLocaleConfig.displayLabelKey"
                [localeKey]="multiLocaleConfig.localeKey"
                [defaultLocale]="multiLocaleConfig.defaultLocale"
                [placeholderText]="descriptionPlaceholder"
                [required]="false"
                [id]="'sdp_description_display_labels'"
                #multiDescriptionlabels
                data-automation="sdpDescription"
            >
            </multi-locale-input>
        </form>
    </div>
</div>

<ng-container *ngIf="isEditMode">
    <div class="service-account" data-automation="serviceAccountSection">
        <div class="form-header" data-automation="serviceAccountHeader">
            <h1>{{ 'utility.account.service_accounts' | translate }}</h1>
            <div
                data-automation="addServiceAccountButton"
                class="add-service-account-button"
                (click)="addServiceAccount()"
                *ngIf="!(loadingServiceAccounts$ | async)"
            >
                <img class="warn-icon" src="./assets/svg/plus-icon.svg" alt="" />
            </div>
        </div>

        <div data-automation="serviceAccountCards" class="service-account-cards" *ngIf="serviceAccounts.length > 0">
            <app-service-accounts
                *ngFor="let serviceAccount of serviceAccounts; let i = index"
                [serviceAccount]="serviceAccount"
                [hasServerError]="serviceAccount.hasServerError"
                [index]="i"
                [mode]="'edit'"
                (remove)="removeServiceAccount($event)"
                (toggle)="toggleServiceAccount($event)"
                (formChange)="onServiceAccountFormChange($event)"
            ></app-service-accounts>
        </div>
        <div class="spinner-container" *ngIf="loadingServiceAccounts$ | async">
            <mat-spinner data-automation="listSpinner" mode="indeterminate" diameter="60" color="primary"></mat-spinner>
        </div>
        <div
            data-automation="noServiceAccountsSection"
            class="no-service-accounts"
            *ngIf="!(loadingServiceAccounts | async) && serviceAccounts.length === 0"
        >
            <mat-icon>close</mat-icon>
            <h1>{{ 'utility.account.view.no_service_accounts' | translate }}</h1>
            <h3 data-automation="noSaConfigured" class="no-sa-configured">
                {{ 'utility.account.view.no_services_configured' | translate }}
            </h3>
        </div>
    </div>
    <!-- EXTERNAL POINTS STARTS -->
    <div class="external-points" data-automation="externalPointsSection">
        <div class="form-header" data-automation="externalPointsHeader">
            <h1>{{ 'utility.account.external_points' | translate }}</h1>
            <div
                [class.disabled]="isAddExternalPointDisabled$ | async"
                data-automation="addExternalPointButton"
                class="add-external-point-button"
                (click)="addExternalPoint()"
                *ngIf="mode === 'edit'"
            >
                <img class="warn-icon" src="./assets/svg/plus-icon.svg" alt="" />
            </div>
        </div>
        <div
            data-automation="externalPointsCards"
            class="external-points-cards"
            *ngIf="!(loadingExternalPoints$ | async) && externalPoints.length > 0"
        >
            <app-external-points
                *ngFor="let externalPoint of externalPoints; let i = index; trackBy: trackByPoint"
                [index]="i"
                [stableKey]="externalPoint.id ?? externalPoint.tempId"
                [hasServerError]="externalPoint.hasServerError"
                [externalPoint]="externalPoint"
                [serviceDeliveryPointDisplayLabels]="this.serviceDeliveryPointForm.get('displayLabels').value"
                [sdpDisplayLabelsByLocale]="sdpDisplayLabelsByLocale"
                [dataProviders]="dataProviders"
                [reportingIntervals]="reportingIntervals"
                [channels]="channels"
                [userLocale]="userLocale"
                [mode]="mode"
                (add)="addExternalPoint()"
                [localeConfig]="multiLocaleConfig"
                [supportedLocales]="serviceDeliveryPointForm.get('supportedLocales').value"
                (remove)="removeExternalPoint($event)"
                (toggle)="toggleExternalPoint($event)"
                (formChange)="onExternalPointFormChange($event)"
                (formValidity)="handleExternalPointFormValidity($event)"
                data-automation="externalPointsCardItem"
            >
            </app-external-points>
        </div>
        <div class="spinner-container" *ngIf="loadingExternalPoints$ | async">
            <mat-spinner data-automation="listSpinner" mode="indeterminate" diameter="60" color="primary"></mat-spinner>
        </div>
        <div
            data-automation="noExternalPointsSection"
            class="no-external-points"
            *ngIf="!(loadingExternalPoints$ | async) && externalPoints.length === 0"
        >
            <div class="no-external-points">
                <mat-icon>close</mat-icon>
                <h1>{{ 'external.point.no_external_points' | translate }}</h1>
                <h3 data-automation="noSaConfigured" class="no-sa-configured">
                    {{ 'external.point.no_points_configured' | translate }}
                </h3>
            </div>
        </div>
    </div>

    <!-- EXTERNAL POINTS ENDS -->
</ng-container>
